// src/components/exercise/exercises/MultipleChoiceExercise.tsx
import React, { useEffect, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { QuestionBox, FeedbackBox } from './TrueFalseExercise';
import { useThemePalette } from '../../../hooks/useThemePalette';
import { useThemeStore }   from '../../../stores/useThemeStore';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

const MultipleChoiceExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [hoveredIdx,  setHoveredIdx]  = useState<number | null>(null);

  useEffect(() => { setSelectedIdx(null); }, [exercise]);

  const pick = (idx: number) => {
    if (isSubmitted) return;
    setSelectedIdx(idx);
    onChange({ type: 'multiple_choice', selectedIndex: idx });
  };

  const getState = (idx: number): 'default' | 'selected' | 'correct' | 'wrong' => {
    if (!isSubmitted) return selectedIdx === idx ? 'selected' : 'default';
    const correctIdx = typeof exercise.correct_answer === 'number' ? exercise.correct_answer : -1;
    if (idx === correctIdx) return 'correct';
    if (idx === selectedIdx && !grade?.isCorrect) return 'wrong';
    return 'default';
  };

  const optionStyle = (idx: number): React.CSSProperties => {
    const state   = getState(idx);
    const hovered = hoveredIdx === idx && !isSubmitted;
    const base: React.CSSProperties = {
      width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
      padding: '13px 16px', borderRadius: '12px', border: '1.5px solid',
      cursor: isSubmitted ? 'default' : 'pointer', textAlign: 'left',
      transition: 'all 0.18s ease', fontSize: '0.92rem', lineHeight: 1.5,
      fontFamily: 'inherit',
    };
    switch (state) {
      case 'selected': return { ...base, background: 'rgba(79,140,247,0.1)',   borderColor: C.accent,   color: C.text };
      case 'correct':  return { ...base, background: 'rgba(52,211,153,0.1)',   borderColor: C.success,  color: C.success };
      case 'wrong':    return { ...base, background: 'rgba(248,113,113,0.08)', borderColor: C.error,    color: C.error };
      default:         return {
        ...base,
        background:  hovered ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)') : C.card,
        borderColor: hovered ? C.borderH : C.border,
        color:       C.muted,
      };
    }
  };

  const bulletBg = (idx: number): string => {
    const state = getState(idx);
    if (state === 'correct')  return C.success;
    if (state === 'wrong')    return C.error;
    if (state === 'selected') return C.accent;
    return isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)';
  };

  const bulletLabel = (idx: number): string => {
    const state = getState(idx);
    if (state === 'correct')  return '✓';
    if (state === 'wrong')    return '✗';
    if (state === 'selected') return '●';
    return String.fromCharCode(65 + idx);
  };

  const bulletTextColor = (idx: number): string => {
    const state = getState(idx);
    if (state === 'correct' || state === 'wrong' || state === 'selected') return '#fff';
    return isDark ? '#fff' : C.text;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(exercise.options ?? []).map((option, idx) => (
          <button
            key={idx}
            style={optionStyle(idx)}
            onClick={() => pick(idx)}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
            disabled={isSubmitted}
            aria-pressed={selectedIdx === idx}
          >
            <span style={{
              minWidth: '24px', height: '24px', borderRadius: '50%',
              background: bulletBg(idx), color: bulletTextColor(idx),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
            }}>
              {bulletLabel(idx)}
            </span>
            <span>{option}</span>
          </button>
        ))}
      </div>
      {isSubmitted && grade && (
        <FeedbackBox
          isCorrect={grade.isCorrect}
          correctDisplay={!grade.isCorrect ? grade.correctAnswerDisplay : undefined}
        />
      )}
    </div>
  );
};

export default MultipleChoiceExercise;
